import React, { useState } from 'react';
import { usePermissions, ROLE_LABELS } from '../hooks/usePermissions';
import { 
  HelpCircle, 
  Book, 
  FileText, 
  Users, 
  Calendar, 
  MapPin, 
  Bell, 
  Shield, 
  PoundSterling,
  ChevronRight,
  ExternalLink,
  Download,
  Rocket,
  Settings,
  Building
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';

// Documentation structure
const userManuals = [
  {
    id: 'medic',
    title: 'Medic User Manual',
    description: 'Complete guide for medics using the portal',
    icon: Users,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    topics: [
      'Logging in and navigating the portal',
      'Viewing and accepting assigned shifts',
      'GPS check-in and check-out',
      'Submitting expenses with receipts',
      'Managing compliance documents',
      'Setting availability preferences',
      'Notification settings'
    ]
  },
  {
    id: 'coordinator',
    title: 'Coordinator & Manager Manual',
    description: 'Operations and team management guide',
    icon: Calendar,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    topics: [
      'Creating and managing shifts',
      'Assigning medics to shifts',
      'Reviewing expenses',
      'Compliance monitoring',
      'Using the Live Map',
      'Generating reports',
      'Audit log access'
    ]
  },
  {
    id: 'admin',
    title: 'Administrator Manual',
    description: 'Full system administration guide',
    icon: Settings,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    topics: [
      'User management and roles',
      'System configuration',
      'Compliance document types',
      'Geofence settings',
      'Email configuration',
      'Notification management',
      'Security and audit'
    ]
  }
];

const featureDocs = [
  {
    id: 'scheduling',
    title: 'Scheduling',
    description: 'Drag-and-drop shift management',
    icon: Calendar,
    color: 'text-teal-500'
  },
  {
    id: 'gps-tracking',
    title: 'GPS Tracking',
    description: 'Live location and geofencing',
    icon: MapPin,
    color: 'text-red-500'
  },
  {
    id: 'push-notifications',
    title: 'Push Notifications',
    description: 'Real-time alerts and reminders',
    icon: Bell,
    color: 'text-yellow-500'
  },
  {
    id: 'compliance',
    title: 'Compliance',
    description: 'Document management and expiry tracking',
    icon: Shield,
    color: 'text-indigo-500'
  },
  {
    id: 'expenses',
    title: 'Expenses',
    description: 'Submission and approval workflow',
    icon: PoundSterling,
    color: 'text-emerald-500'
  }
];

const quickStartGuides = [
  {
    id: 'getting-started',
    title: 'Getting Started with Elara',
    description: 'First steps after logging in',
    icon: Rocket
  },
  {
    id: 'organisation-setup',
    title: 'Setting Up Your Organisation',
    description: 'Initial configuration guide',
    icon: Building
  },
  {
    id: 'adding-staff',
    title: 'Adding Staff & Roles',
    description: 'User and medic management',
    icon: Users
  },
  {
    id: 'first-shifts',
    title: 'Creating Your First Shifts',
    description: 'Shift creation walkthrough',
    icon: Calendar
  },
  {
    id: 'notifications',
    title: 'Understanding Notifications',
    description: 'Alert types and preferences',
    icon: Bell
  },
  {
    id: 'live-map',
    title: 'Using the Live Map',
    description: 'Real-time tracking guide',
    icon: MapPin
  }
];

export default function HelpSupport() {
  const { isMedic, isOpsCoordinator, isManager, isAdmin } = usePermissions();
  const [activeTab, setActiveTab] = useState('overview');
  
  // Base URL for documentation files
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';
  const docsBaseUrl = `${API_URL}/api/docs-static`;

  // Determine which manual to highlight based on user role
  const getRecommendedManual = () => {
    if (isMedic) return 'medic';
    if (isOpsCoordinator) return 'coordinator';
    if (isManager) return 'coordinator';
    if (isAdmin) return 'admin';
    return 'medic';
  };

  const recommendedManual = getRecommendedManual();

  return (
    <div className="space-y-6" data-testid="help-support-page">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold text-[#F2F2F2] flex items-center gap-2">
          <HelpCircle className="w-7 h-7 text-[#1ABC9C]" />
          Help & Support
        </h1>
        <p className="text-[#A1A1AA]">
          Documentation, user guides, and quick-start tutorials for Elara Workforce Management
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-[#2D2D2D] border border-[#3D3D3D]">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]">
            Overview
          </TabsTrigger>
          <TabsTrigger value="manuals" className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]">
            User Manuals
          </TabsTrigger>
          <TabsTrigger value="features" className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]">
            Feature Guides
          </TabsTrigger>
          <TabsTrigger value="quickstart" className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]">
            Quick Start
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Recommended for You */}
            <Card className="bg-[#2D2D2D] border-[#1ABC9C]/50 md:col-span-2 lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                  <Book className="w-5 h-5 text-[#1ABC9C]" />
                  Recommended for You
                </CardTitle>
                <CardDescription className="text-[#A1A1AA]">
                  Based on your role
                </CardDescription>
              </CardHeader>
              <CardContent>
                {userManuals.filter(m => m.id === recommendedManual).map(manual => {
                  const Icon = manual.icon;
                  return (
                    <div key={manual.id} className={`p-4 rounded-lg ${manual.bgColor} border border-[#3D3D3D]`}>
                      <div className="flex items-start gap-3">
                        <Icon className={`w-6 h-6 ${manual.color} mt-0.5`} />
                        <div className="flex-1">
                          <h3 className="font-semibold text-[#F2F2F2]">{manual.title}</h3>
                          <p className="text-sm text-[#A1A1AA] mt-1">{manual.description}</p>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="mt-3 text-[#1ABC9C] hover:text-[#1ABC9C] hover:bg-[#1ABC9C]/10 p-0 h-auto"
                            onClick={() => setActiveTab('manuals')}
                          >
                            Read Manual <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="bg-[#2D2D2D] border-[#3D3D3D]">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-yellow-500" />
                  Quick Start Guides
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {quickStartGuides.slice(0, 4).map(guide => {
                      const Icon = guide.icon;
                      return (
                        <button
                          key={guide.id}
                          onClick={() => setActiveTab('quickstart')}
                          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors text-left"
                        >
                          <Icon className="w-4 h-4 text-[#A1A1AA]" />
                          <span className="text-sm text-[#C7C7C7]">{guide.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Feature Documentation */}
            <Card className="bg-[#2D2D2D] border-[#3D3D3D]">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-500" />
                  Feature Guides
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {featureDocs.map(doc => {
                      const Icon = doc.icon;
                      return (
                        <button
                          key={doc.id}
                          onClick={() => setActiveTab('features')}
                          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors text-left"
                        >
                          <Icon className={`w-4 h-4 ${doc.color}`} />
                          <div>
                            <span className="text-sm text-[#C7C7C7] block">{doc.title}</span>
                            <span className="text-xs text-[#A1A1AA]">{doc.description}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Contact Support */}
          <Card className="bg-[#2D2D2D] border-[#3D3D3D] mt-6">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#1ABC9C]/20 flex items-center justify-center">
                    <HelpCircle className="w-6 h-6 text-[#1ABC9C]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#F2F2F2]">Need more help?</h3>
                    <p className="text-sm text-[#A1A1AA]">Contact your system administrator for additional support</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="border-[#3D3D3D] text-[#C7C7C7] hover:bg-white/5"
                  onClick={() => window.location.href = 'mailto:support@elarasystems.co.uk'}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Manuals Tab */}
        <TabsContent value="manuals" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {userManuals.map(manual => {
              const Icon = manual.icon;
              const isRecommended = manual.id === recommendedManual;
              return (
                <Card 
                  key={manual.id} 
                  className={`bg-[#2D2D2D] ${isRecommended ? 'border-[#1ABC9C]/50 ring-1 ring-[#1ABC9C]/30' : 'border-[#3D3D3D]'}`}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className={`w-10 h-10 rounded-lg ${manual.bgColor} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${manual.color}`} />
                      </div>
                      {isRecommended && (
                        <span className="text-xs bg-[#1ABC9C]/20 text-[#1ABC9C] px-2 py-1 rounded-full">
                          Recommended
                        </span>
                      )}
                    </div>
                    <CardTitle className="text-[#F2F2F2] mt-3">{manual.title}</CardTitle>
                    <CardDescription className="text-[#A1A1AA]">{manual.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-4">
                      <p className="text-xs text-[#A1A1AA] uppercase tracking-wide">Topics covered:</p>
                      <ul className="space-y-1">
                        {manual.topics.slice(0, 5).map((topic, idx) => (
                          <li key={idx} className="text-sm text-[#C7C7C7] flex items-center gap-2">
                            <div className="w-1 h-1 rounded-full bg-[#1ABC9C]" />
                            {topic}
                          </li>
                        ))}
                        {manual.topics.length > 5 && (
                          <li className="text-sm text-[#A1A1AA] pl-3">
                            +{manual.topics.length - 5} more topics
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 border-[#3D3D3D] text-[#C7C7C7] hover:bg-white/5"
                        onClick={() => window.open(`${docsBaseUrl}/user-manuals/${manual.id}-manual.md`, '_blank')}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="flex-1 border-[#3D3D3D] text-[#C7C7C7] hover:bg-white/5"
                        onClick={() => window.open(`${docsBaseUrl}/pdf/user-manuals/${manual.id}-manual.pdf`, '_blank')}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Feature Guides Tab */}
        <TabsContent value="features" className="mt-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {featureDocs.map(doc => {
              const Icon = doc.icon;
              return (
                <Card key={doc.id} className="bg-[#2D2D2D] border-[#3D3D3D] hover:border-[#1ABC9C]/30 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                        <Icon className={`w-5 h-5 ${doc.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#F2F2F2]">{doc.title}</h3>
                        <p className="text-sm text-[#A1A1AA] mt-1">{doc.description}</p>
                        <div className="flex gap-2 mt-3">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-[#1ABC9C] hover:text-[#1ABC9C] hover:bg-[#1ABC9C]/10 p-0 h-auto"
                            onClick={() => window.open(`${docsBaseUrl}/feature-docs/${doc.id}.md`, '_blank')}
                          >
                            Read Guide <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Architecture Documentation - Admin Only */}
          {isAdmin && (
            <Card className="bg-[#2D2D2D] border-[#3D3D3D] mt-6">
              <CardHeader>
                <CardTitle className="text-[#F2F2F2] flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-500" />
                  System Architecture
                </CardTitle>
                <CardDescription className="text-[#A1A1AA]">
                  Technical documentation for administrators
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-[#3D3D3D] text-[#C7C7C7] hover:bg-white/5"
                    onClick={() => window.open(`${docsBaseUrl}/architecture/system-overview.md`, '_blank')}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    System Overview
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-[#3D3D3D] text-[#C7C7C7] hover:bg-white/5"
                    onClick={() => window.open(`${docsBaseUrl}/README.md`, '_blank')}
                  >
                    <Book className="w-4 h-4 mr-2" />
                    Documentation Index
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Quick Start Tab */}
        <TabsContent value="quickstart" className="mt-6">
          <div className="grid gap-4 md:grid-cols-2">
            {quickStartGuides.map((guide, index) => {
              const Icon = guide.icon;
              return (
                <Card key={guide.id} className="bg-[#2D2D2D] border-[#3D3D3D]">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-8 h-8 rounded-full bg-[#1ABC9C]/20 flex items-center justify-center shrink-0 text-[#1ABC9C] font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className="w-4 h-4 text-[#A1A1AA]" />
                          <h3 className="font-semibold text-[#F2F2F2]">{guide.title}</h3>
                        </div>
                        <p className="text-sm text-[#A1A1AA]">{guide.description}</p>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="mt-2 text-[#1ABC9C] hover:text-[#1ABC9C] hover:bg-[#1ABC9C]/10 p-0 h-auto"
                          onClick={() => window.open(`${docsBaseUrl}/quick-start/${guide.id}.md`, '_blank')}
                        >
                          Start Guide <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
